// A scene's solid colours, and the ink its line takes over each one. Shared by
// the composer's SceneView and the reader, so a custom colour reads the same
// in both.

/**
 * The swatches, from Burrow's own theme (src/styles/theme.css): primary plum
 * (the default), foreground ink, muted taupe, the rose accent, and the cream
 * background. Anything else comes from the custom picker; saved scenes keep
 * whatever they chose.
 */
export const SCENE_COLORS: Array<[string, string]> = [
  ['#4A1525', 'Plum'],
  ['#2D1B1E', 'Ink'],
  ['#8A7668', 'Taupe'],
  ['#D4848B', 'Rose'],
  ['#F3EAE0', 'Cream'],
]

export const DEFAULT_SCENE_COLOR = SCENE_COLORS[0][0]

/**
 * The wash over a scene's photo, darkest in the middle where the line sits,
 * so the line reads over any picture (composer and reader alike).
 */
export const SCENE_SHADE =
  'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(45,27,30,.58),rgba(45,27,30,.3)_70%,rgba(45,27,30,.22))]'

export const LIGHT_INK = '#FDF8F3'
export const DARK_INK = '#2D1B1E'

/** "#abc" or "#aabbcc" as 0..255 channels; null for anything else. */
function rgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const h = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1]
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number]
}

/** WCAG relative luminance. */
function luminance([r, g, b]: [number, number, number]): number {
  const lin = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

/**
 * The line's colour over a solid scene: Burrow's cream on deep colours, its
 * ink on light ones (rose, cream, anything picked pale) — whichever has the
 * stronger contrast. Unreadable input falls back to cream.
 */
export function sceneInk(color: string | undefined): string {
  const c = rgb(color ?? DEFAULT_SCENE_COLOR)
  if (!c) return LIGHT_INK
  const l = luminance(c)
  const light = (luminance(rgb(LIGHT_INK)!) + 0.05) / (l + 0.05)
  const dark = (l + 0.05) / (luminance(rgb(DARK_INK)!) + 0.05)
  return dark > light ? DARK_INK : LIGHT_INK
}
